import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { classService, getErrorMessage, getErrorStatus } from '../services/api';
import { AlertCircle } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { LoadingState } from '../components/ui';
import NotFound from './NotFound';
import {
  clearPendingInviteCode,
  normalizeInviteCode,
  setPendingInviteCode,
} from '../utils/invite';

const InviteEntry: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [typedCode, setTypedCode] = useState('');

  const normalizedInviteCode = useMemo(() => normalizeInviteCode(inviteCode), [inviteCode]);

  useEffect(() => {
    if (loading || !normalizedInviteCode) {
      return;
    }

    let cancelled = false;
    setError('');

    const processInvite = async () => {
      try {
        await classService.getClassByInviteCode(normalizedInviteCode);
      } catch (err) {
        if (!cancelled) {
          setError(getErrorStatus(err) === 404
            ? 'This invite link is invalid or has expired.'
            : getErrorMessage(err, 'This invite link is invalid or has expired.'));
        }
        return;
      }

      if (!user) {
        setPendingInviteCode(normalizedInviteCode);
        navigate(`/register?invite=${normalizedInviteCode}`, { replace: true });
        return;
      }

      if (user.role !== 'student') {
        setError('Invite links can only be used with student accounts.');
        return;
      }

      try {
        await classService.joinClassByInviteCode(normalizedInviteCode);
        clearPendingInviteCode();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err, 'Unable to join this class from the invite link.'));
        }
      }
    };

    processInvite();

    return () => {
      cancelled = true;
    };
  }, [loading, navigate, normalizedInviteCode, user]);

  // Not shaped like an invite code (e.g. a mistyped page): a plain 404, not an invite error.
  if (!normalizedInviteCode) {
    return <NotFound />;
  }

  if (!error) {
    return (
      <AuthShell brandAsHeading={false}>
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Processing Invite</h1>
          <LoadingState label="Connecting you to your class..." className="py-4" />
        </div>
      </AuthShell>
    );
  }

  const typedInviteCode = normalizeInviteCode(typedCode);

  return (
    <AuthShell brandAsHeading={false}>
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Invite Error</h1>
      <div className="alert alert-error mb-4" role="alert">
        <AlertCircle />
        <span>{error}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
        Professors can replace a class's invite link, which turns off the old one. Ask your professor for the current
        link, or type the class code below.
      </p>
      {(!user || user.role === 'student') && (
        <form
          className="flex gap-2 mb-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (typedInviteCode) {
              navigate(`/${typedInviteCode}`);
            }
          }}
        >
          <input
            type="text"
            value={typedCode}
            onChange={(event) => setTypedCode(event.target.value.toUpperCase())}
            placeholder="ABC12345"
            maxLength={12}
            aria-label="Class code"
            className="input min-w-0 flex-1 font-mono tracking-widest uppercase"
          />
          <button type="submit" disabled={!typedInviteCode} className="btn btn-primary shrink-0">
            Join
          </button>
        </form>
      )}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
        {user ? (
          <Link to="/dashboard" className="btn btn-primary flex-1">
            Back to Dashboard
          </Link>
        ) : (
          <>
            <Link to="/register" className="btn btn-secondary flex-1">
              Register
            </Link>
            <Link to="/login" className="btn btn-primary flex-1">
              Go to Login
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
};

export default InviteEntry;
